import React, {
  memo, useRef, useCallback, useEffect,
} from 'react'
import Check from '../../../../Icon/icons/Check/index'

/**
 * Custom-minutes time card on the smart-time step.
 *
 * The card has two distinct text slots — a primary label and a separate
 * `<span class="create-playlist-modal__time-custom-unit">` unit slot —
 * rendered as siblings, NOT a templated string.
 *
 * Behavior preserved verbatim from the inline version:
 *  - Click anywhere on the card selects it AND focuses the input.
 *  - Enter / Space on the card does the same (role="button").
 *  - Input onChange parses via `parseInt(raw, 10)` with Number.isFinite
 *    guard, returning null on NaN / empty.
 *  - Input bounds: min={1}, max={300}.
 *  - Typing in the input auto-selects the card via the parent's onChange.
 *  - When `isSelected` toggles true, the input gains focus on the next
 *    commit (via useEffect) — useful when selection is driven by an
 *    upstream state change rather than a direct card click.
 */
export interface TimeCustomCardProps {
  isSelected: boolean
  customMinutes: number | null
  onSelect: () => void
  onChange: (value: number | null) => void
  placeholder: string
  ariaLabel: string
  primaryLabel: string
  unitLabel: string
  /** Caller-provided per-key className (border-color modifier). */
  cardClassName: string
}

function TimeCustomCardBase ({
  isSelected,
  customMinutes,
  onSelect,
  onChange,
  placeholder,
  ariaLabel,
  primaryLabel,
  unitLabel,
  cardClassName,
}: TimeCustomCardProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  const selectAndFocus = useCallback(() => {
    onSelect()
    inputRef.current?.focus()
  }, [onSelect])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      selectAndFocus()
    }
  }, [selectAndFocus])

  // External selection (e.g. user typed in the input → parent state flips
  // selectedTime to TIME_CUSTOM_KEY) → focus the input on the next commit.
  useEffect(() => {
    if (isSelected) inputRef.current?.focus()
  }, [isSelected])

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = parseInt(e.target.value, 10)
    onChange(Number.isFinite(parsed) ? parsed : null)
  }, [onChange])

  return (
    <div
      className={cardClassName}
      role="button"
      tabIndex={0}
      onClick={selectAndFocus}
      onKeyDown={handleKeyDown}
    >
      <span className="create-playlist-modal__time-primary">
        {primaryLabel}
        {isSelected ? (
          <Check
            size="16px"
            color="#137F89"
            className="create-playlist-modal__time-check"
          />
        ) : null}
      </span>
      <div
        className="create-playlist-modal__time-custom-input-wrap"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          type="number"
          className="create-playlist-modal__time-custom-input"
          value={customMinutes ?? ''}
          onChange={handleInputChange}
          placeholder={placeholder}
          min={1}
          max={300}
          aria-label={ariaLabel}
        />
        <span className="create-playlist-modal__time-custom-unit">{unitLabel}</span>
      </div>
    </div>
  )
}

const TimeCustomCard = memo(TimeCustomCardBase)
TimeCustomCard.displayName = 'TimeCustomCard'
export default TimeCustomCard
